import React, { useEffect } from "react";
import { Spin, notification } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../base/services/authentication.service";
import { describeSavedCard } from "./billingActions";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { getStoredBillingStatus, isBillingOnly, useBillingStore } from "./billingStore";
import { readDecline, settlePayment } from "./paymentAction";
import "./index.scss";

/**
 * Where Stripe sends the browser back to when a bank's check had to leave the
 * page (CardSetup's `return_url`). It carries the SetupIntent in the address —
 * `setup_intent`, `redirect_status` — so the card is completed here the way the
 * form would have completed it, and the browser goes back to where the card was
 * being saved: the billing page, or the subscription page for an organization
 * that was never held.
 *
 * The setup_intent.succeeded webhook completes the same card on its own, so a
 * failure here is reported but not the last word: the status read at the end
 * says where things stand.
 */
function CardSavedPage() {
   const navigate = useNavigate();
   const { search } = useLocation();

   useEffect(() => {
      let isCurrent = true;
      const organizationId = AuthService.getOrganizationId();
      const params = new URLSearchParams(search);
      const setupIntentId = params.get("setup_intent");
      const redirectStatus = params.get("redirect_status");

      const settle = async () => {
         if (setupIntentId && redirectStatus !== "failed") {
            try {
               // As the card form settles it (CardSetup): a decline comes back as
               // 409 PAYMENT_FAILED with the card already saved, and an annual
               // plan's other overdue months are paid with it too.
               let settled;
               let completed;
               try {
                  completed = await billingApi.completeSetupIntent({
                     organizationId,
                     setupIntentId
                  });
               } catch (failure) {
                  settled = readDecline(failure);
                  // The bank wants 3-D Secure on the payment: settled like any
                  // status that asks for action.
                  if (!settled && failure.code !== BillingCode.PAYMENT_ACTION_REQUIRED) throw failure;
                  completed = { requiresAction: true };
               }
               settled = settled ?? (await settlePayment(completed, organizationId));

               // The card is on file either way; what the bank decided about the
               // payment it was then asked to make is what decides the message,
               // and with no subscription for it to pay, a plan comes next.
               if (settled.error) {
                  notification.error({
                     message: "Oops! A problem has occurred!",
                     description:
                        "Your card was saved, but your bank didn't approve the payment, so it's still due."
                  });
               } else {
                  notification.success({
                     message: "Card saved successfully",
                     description: describeSavedCard(settled.status)
                  });
               }
            } catch (failure) {
               notification.error({
                  message: "Oops! A problem has occurred!",
                  description: describeBillingError(failure)
               });
            }
         } else if (redirectStatus === "failed") {
            notification.error({
               message: "Oops! A problem has occurred!",
               description: "Your bank didn't confirm the card. Please try again, or use a different card."
            });
         }

         try {
            await billingApi.getBillingStatus(organizationId);
         } catch (unreadable) {
            // The last status known is used instead.
         }

         if (!isCurrent) return;

         const held = isBillingOnly(getStoredBillingStatus(organizationId));
         const canOpenSubscription = useBillingStore.getState().canManageBilling;

         navigate(held ? "/billing" : canOpenSubscription ? "/subscription-plans" : "/", {
            replace: true
         });
      };

      settle();

      return () => {
         isCurrent = false;
      };
      // Once, for the address it was opened at.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <div className="billing-loading billing-loading--page">
         <Spin size="large" />
         <p>Finishing up with your card…</p>
      </div>
   );
}

export default CardSavedPage;
